// Gravure de la partition avec VexFlow (police Bravura), sur une seule ligne qui défile
// horizontalement. Le graveur produit aussi les repères nécessaires à la synchronisation :
// position horizontale de chaque note et de chaque barre de mesure (ancres du curseur),
// cadre de chaque note (mise en évidence) et identifiant de l'élément SVG de chaque
// événement (coloration selon le résultat, sans regraver).

import { Accidental, Annotation, BarlineType, Beam, Dot, Formatter, FretHandFinger, Modifier, Renderer, Stave, StaveConnector, StaveNote, StaveTie, Voice } from 'vexflow/bravura';
import type { NoteState } from '../../engine/session';
import { noteName, type NamingSystem } from '../../music/pitchNames';
import { vexDuration, vexKey, type SheetEvent, type SheetModel, type SheetStaff } from '../../music/sheetModel';
import { normalizeAnchors, type CursorAnchor } from './cursorMap';

export interface SheetRenderOptions {
  showFingers: boolean;
  showNames: boolean;
  naming: NamingSystem;
  /** Facteur d'échelle appliqué à toute la gravure. */
  scale: number;
}

export interface SheetNoteBox {
  eventKey: string;
  noteIds: string[];
  pitches: number[];
  tick: number;
  /** Cadre en unités gravées (avant échelle). */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SheetRenderResult {
  /** Dimensions gravées, avant échelle. */
  width: number;
  height: number;
  scale: number;
  anchors: CursorAnchor[];
  boxes: SheetNoteBox[];
  measureStarts: { tick: number; x: number }[];
}

// VexFlow réserve quatre interlignes (40 unités) au-dessus de la première ligne d'une portée ;
// on garde en dessous de quoi loger deux lignes supplémentaires et les annotations.
const STAVE_TOP = 28;
const STAVE_GAP = 150;
const STAVE_BLOCK = 166;
const MARGIN = 12;
const MEASURE_BASE_WIDTH = 150;
const STATE_CLASSES: NoteState[] = ['pending', 'correct', 'early', 'late', 'wrong', 'wrongOctave', 'extra', 'missed', 'uncertain'];

const KEY_NAMES: Record<number, string> = { 0: 'C', 1: 'G', 2: 'D', 3: 'A', 4: 'E', 5: 'B', 6: 'F#', 7: 'C#', '-1': 'F', '-2': 'Bb', '-3': 'Eb', '-4': 'Ab', '-5': 'Db', '-6': 'Gb', '-7': 'Cb' };

/** Hauteur gravée (avant échelle) pour un nombre de portées. */
export function naturalSheetHeight(staffCount: number): number {
  return STAVE_TOP + (Math.max(1, staffCount) - 1) * STAVE_GAP + STAVE_BLOCK;
}

/** Priorité d'affichage quand un accord réunit des notes dans des états différents. */
function combineStates(states: NoteState[]): NoteState {
  const order: NoteState[] = ['wrong', 'wrongOctave', 'missed', 'extra', 'uncertain', 'late', 'early', 'correct', 'pending'];
  for (const s of order) if (states.includes(s)) return s;
  return 'pending';
}

function restKey(staff: SheetStaff, whole: boolean): string {
  if (staff.clef === 'treble') return whole ? 'd/5' : 'b/4';
  return whole ? 'f/3' : 'd/3';
}

interface Built {
  notes: StaveNote[];
  events: { note: StaveNote; event: SheetEvent; key: string }[];
  ties: [StaveNote, StaveNote][];
}

function buildStaffMeasure(staff: SheetStaff, measureIndex: number, options: SheetRenderOptions): Built {
  const measure = staff.measures[measureIndex];
  const notes: StaveNote[] = [];
  const events: Built['events'] = [];
  const ties: Built['ties'] = [];
  let previous: { note: StaveNote; event: SheetEvent } | null = null;
  measure.events.forEach((event, i) => {
    const key = `${staff.hand}-m${measureIndex}-e${i}`;
    let note: StaveNote;
    if (event.kind === 'rest') {
      note = new StaveNote({ keys: [restKey(staff, event.wholeMeasure === true)], duration: vexDuration(event), clef: staff.clef, align_center: event.wholeMeasure === true });
    } else {
      note = new StaveNote({ keys: event.notes.map((n) => vexKey(n.pitch)), duration: vexDuration(event), clef: staff.clef, auto_stem: true });
      const chord = event.notes.length > 1;
      event.notes.forEach((n, index) => {
        if (options.showFingers && n.finger !== undefined) {
          if (chord) {
            // Accord : un doigté à gauche de chaque tête de note, à sa hauteur.
            const finger = new FretHandFinger(String(n.finger)).setPosition(Modifier.Position.LEFT);
            note.addModifier(finger, index);
          } else {
            // Note isolée : doigté au-dessus (les deux mains), nom en dessous.
            const finger = new Annotation(String(n.finger)).setFont('Source Sans 3, Segoe UI, sans-serif', 10, 'bold');
            finger.setVerticalJustification(Annotation.VerticalJustify.TOP);
            note.addModifier(finger, index);
          }
        }
        if (options.showNames && index === 0 && !chord) {
          const name = new Annotation(noteName(n.pitch, options.naming)).setFont('Source Sans 3, Segoe UI, sans-serif', 9, 'normal');
          name.setVerticalJustification(Annotation.VerticalJustify.BOTTOM);
          note.addModifier(name, 0);
        }
      });
    }
    if (event.dots > 0) Dot.buildAndAttach([note], { all: true });
    note.setAttribute('id', key);
    notes.push(note);
    events.push({ note, event, key });
    if (previous && previous.event.tieToNext && previous.event.kind === 'note' && event.kind === 'note') ties.push([previous.note, note]);
    previous = { note, event };
  });
  return { notes, events, ties };
}

export class SheetRenderer {
  private readonly host: HTMLDivElement;
  private eventGroups = new Map<string, SVGGElement>();
  private eventNotes = new Map<string, string[]>();
  private noteToEvent = new Map<string, string>();

  constructor(host: HTMLDivElement) {
    this.host = host;
  }

  /** Grave le modèle dans le conteneur (remplace le rendu précédent). */
  render(model: SheetModel, options: SheetRenderOptions): SheetRenderResult {
    this.host.innerHTML = '';
    this.eventGroups.clear();
    this.eventNotes.clear();
    this.noteToEvent.clear();
    const renderer = new Renderer(this.host, Renderer.Backends.SVG);
    const height = naturalSheetHeight(model.staves.length);
    // Taille provisoire (largeur large) : VexFlow fixe la boîte de vue à partir de l'échelle.
    // La taille définitive est posée directement sur l'élément SVG après gravure, car un
    // second appel à resize() multiplierait l'échelle une seconde fois.
    renderer.resize(4000 * options.scale, height * options.scale);
    const ctx = renderer.getContext();
    ctx.scale(options.scale, options.scale);

    const anchors: CursorAnchor[] = [];
    const boxes: SheetNoteBox[] = [];
    const measureStarts: { tick: number; x: number }[] = [];
    const ts = model.timeSignature;
    const keyName = KEY_NAMES[model.keyFifths] ?? 'C';
    let x = MARGIN;

    for (let m = 0; m < model.measureCount; m++) {
      const first = m === 0;
      const last = m === model.measureCount - 1;
      const built = model.staves.map((staff) => buildStaffMeasure(staff, m, options));
      const voices = built.map((b) => new Voice({ num_beats: ts.beats, beat_value: ts.beatUnit }).setStrict(false).addTickables(b.notes));
      voices.forEach((v) => Accidental.applyAccidentals([v], keyName));
      const formatter = new Formatter();
      formatter.joinVoices(voices);
      const minWidth = formatter.preCalculateMinTotalWidth(voices);
      const noteArea = Math.max(MEASURE_BASE_WIDTH, minWidth * 1.3 + 40);

      const staves = model.staves.map((staff, i) => {
        const stave = new Stave(x, STAVE_TOP + i * STAVE_GAP, 100);
        if (i === 0) stave.setMeasure(m + 1);
        if (first) {
          stave.addClef(staff.clef);
          if (model.keyFifths !== 0) stave.addKeySignature(keyName);
          stave.addTimeSignature(`${ts.beats}/${ts.beatUnit}`);
        }
        if (last) stave.setEndBarType(BarlineType.END);
        return stave;
      });
      const startOffset = staves.length > 0 ? staves[0].getNoteStartX() - x : 0;
      const width = startOffset + noteArea + 12;
      staves.forEach((s) => {
        s.setWidth(width);
        s.setContext(ctx).draw();
      });
      if (staves.length > 1) {
        if (first) new StaveConnector(staves[0], staves[staves.length - 1]).setType(StaveConnector.type.BRACE).setContext(ctx).draw();
        new StaveConnector(staves[0], staves[staves.length - 1]).setType(StaveConnector.type.SINGLE_LEFT).setContext(ctx).draw();
        new StaveConnector(staves[0], staves[staves.length - 1]).setType(last ? StaveConnector.type.BOLD_DOUBLE_RIGHT : StaveConnector.type.SINGLE_RIGHT).setContext(ctx).draw();
      }
      if (staves.length > 0) formatter.formatToStave(voices, staves[0]);
      const beams = built.flatMap((b) => Beam.generateBeams(b.notes));
      voices.forEach((v, i) => v.draw(ctx, staves[i]));
      beams.forEach((b) => b.setContext(ctx).draw());
      built.forEach((b) => b.ties.forEach(([from, to]) => new StaveTie({ first_note: from, last_note: to }).setContext(ctx).draw()));

      const measureTick = m * model.ticksPerMeasure;
      measureStarts.push({ tick: measureTick, x: staves.length > 0 ? staves[0].getNoteStartX() : x });
      anchors.push({ tick: measureTick, x: staves.length > 0 ? staves[0].getNoteStartX() : x });
      anchors.push({ tick: measureTick + model.ticksPerMeasure, x: x + width - 8 });
      for (const b of built) {
        for (const { note, event, key } of b.events) {
          const absX = note.getAbsoluteX();
          // Le curseur vise le centre de la tête de note (largeur de tête ≈ 10 unités). Un silence de
          // mesure entière est gravé au centre de la mesure : position décorative, pas temporelle,
          // donc pas d'ancre (le début de mesure sert de repère).
          if (!(event.kind === 'rest' && event.wholeMeasure)) anchors.push({ tick: event.tick, x: absX + 5 });
          const bb = note.getBoundingBox();
          const box: SheetNoteBox = {
            eventKey: key,
            noteIds: event.notes.map((n) => n.id),
            pitches: event.notes.map((n) => n.pitch),
            tick: event.tick,
            x: bb ? bb.getX() : absX - 8,
            y: bb ? bb.getY() : 0,
            w: bb ? bb.getW() : 16,
            h: bb ? bb.getH() : 40,
          };
          if (event.kind === 'note') boxes.push(box);
          this.eventNotes.set(key, box.noteIds);
          for (const id of box.noteIds) if (!this.noteToEvent.has(id)) this.noteToEvent.set(id, key);
          const group = this.host.querySelector<SVGGElement>(`#vf-${key}`);
          if (group) this.eventGroups.set(key, group);
        }
      }
      x += width;
    }
    const width = x + MARGIN;
    const svg = this.host.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', String(width * options.scale));
      svg.setAttribute('height', String(height * options.scale));
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.style.width = `${width * options.scale}px`;
      svg.style.height = `${height * options.scale}px`;
    }
    this.host.style.width = `${width * options.scale}px`;
    return { width, height, scale: options.scale, anchors: normalizeAnchors(anchors), boxes, measureStarts };
  }

  /** Colore chaque événement selon l'état de ses notes (classe CSS sur le groupe SVG). */
  applyStates(states: ReadonlyMap<string, NoteState>): void {
    for (const [key, group] of this.eventGroups) {
      const ids = this.eventNotes.get(key) ?? [];
      if (ids.length === 0) continue;
      const state = combineStates(ids.map((id) => states.get(id) ?? 'pending'));
      for (const s of STATE_CLASSES) group.classList.remove(`st-${s}`);
      group.classList.add(`st-${state}`);
    }
  }

  /** Marque les événements attendus (mode attente). */
  applyExpected(noteIds: readonly string[]): void {
    const keys = new Set(noteIds.map((id) => this.noteToEvent.get(id)).filter((k): k is string => k !== undefined));
    for (const [key, group] of this.eventGroups) {
      if (keys.has(key)) group.classList.add('st-expected');
      else group.classList.remove('st-expected');
    }
  }
}
